import React, { useState, useEffect, useRef } from 'react';
import { IconButton, CircularProgress, Box, Typography } from '@mui/material';
import {
  WarningRounded as WarningRoundedIcon,
  HistoryToggleOffRounded as HistoryToggleOffRoundedIcon,
  CheckRounded as CheckRoundedIcon,
  SlowMotionVideoRounded as SlowMotionVideoRoundedIcon,
  PlayArrowRounded as PlayArrowRoundedIcon,
  PauseRounded as PauseRoundedIcon,
} from '@mui/icons-material';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import sanitize from 'sanitize-html';
import { CONVERTING_STATUS } from '@src/constants/project';
import Tooltip from '@src/components/Tooltip';
import actions from '@src/redux/actions';
import useUserInfo from '@src/hooks/useTtsUser';
import { COLOR } from '@src/styles/color';

const SynthesisButton = ({
  isDisableConvertBtn,
  status,
  isPlaying,
  blockId,
  showSuccessIcon,
  audioExpiredAt,
  checkValidCreateSynthesis,
  handlePlayAudio,
  isDisablePlayAudio,
  onConvertBlockToAudio,
  isBlockEditting,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { multiBlock } = useSelector((state) => state.project);
  const { ttsUser } = useUserInfo();
  const userAudioRetentionPeriod = ttsUser.retentionPeriod;
  const isInitialMount = useRef(true);

  const blocks = multiBlock?.blocks;
  const isSynthesisInProgress = status === CONVERTING_STATUS.IN_PROGRESS;
  const isSynthesisSuccess = status === CONVERTING_STATUS.SUCCESS;
  const isSynthesisFailure = status === CONVERTING_STATUS.FAILURE;
  const isAudioExpired = audioExpiredAt && moment().isAfter(audioExpiredAt);
  const isEnableConvertBtn =
    isAudioExpired || isSynthesisFailure || !isDisableConvertBtn;

  const [tempSuccessIcon, setTempSuccessIcon] = useState(false);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    if (showSuccessIcon) {
      setTempSuccessIcon(true);
      setTimeout(() => {
        setTempSuccessIcon(false);
      }, 3000);
    }
  }, [showSuccessIcon]);

  const handleConvertBlockToAudio = async () => {
    if (!checkValidCreateSynthesis(blocks[blockId])) return;
    onConvertBlockToAudio(blocks[blockId]);
  };
  const handlePlayAudioInBlock = () => {
    if (handlePlayAudio) handlePlayAudio(blockId, audioExpiredAt);
  };

  const handleStopAudio = () => {
    dispatch(actions.project.setIsPlayingBlock(false));
  };

  const renderSynthesisStatusIcon = () => {
    if (isAudioExpired && isSynthesisSuccess) {
      return (
        <Tooltip
          arrow
          title={
            <Typography
              component="span"
              sx={{ fontSize: '14px', textAlign: 'left' }}
              style={{ textAlign: 'left' }}
              dangerouslySetInnerHTML={{
                __html: sanitize(
                  t('blockAudioExpired', {
                    audioRetentionPeriod: userAudioRetentionPeriod,
                  }),
                  {
                    allowedTags: ['span'],
                    allowedAttributes: {
                      span: ['style'],
                    },
                  },
                ),
              }}
            />
          }
          placement="left"
        >
          <HistoryToggleOffRoundedIcon
            className="icon"
            sx={{ color: COLOR.redPrimary }}
          />
        </Tooltip>
      );
    }

    if (isSynthesisFailure) {
      return (
        <Tooltip
          maxWidth="100%"
          arrow
          title={t('blockSynthesisFailure')}
          placement="left"
        >
          <WarningRoundedIcon
            sx={{
              color: COLOR.danger,
              fontSize: 20,
              backgroundColor: 'rgba(30, 22, 78, 0.08)',
              borderRadius: '8px',
            }}
            className="icon"
          />
        </Tooltip>
      );
    }

    return null;
  };

  const renderPlayOrPauseAudioButton = () => {
    if (isPlaying) {
      return (
        <Tooltip arrow title={t('pause')} placement="top">
          <IconButton onClick={handleStopAudio} className="editor-toolbar-btn">
            <PauseRoundedIcon />
          </IconButton>
        </Tooltip>
      );
    }
    return (
      <Tooltip
        sx={{ display: isDisablePlayAudio ? 'none' : 'block' }}
        arrow
        title={t('playAudio')}
        placement="top"
      >
        <div className={isDisablePlayAudio ? 'disabled-btn' : ''}>
          <IconButton
            onClick={handlePlayAudioInBlock}
            className="editor-toolbar-btn"
            disabled={isDisablePlayAudio}
          >
            <PlayArrowRoundedIcon />
          </IconButton>
        </div>
      </Tooltip>
    );
  };

  const renderSynthesisButton = () => {
    if (isSynthesisInProgress) {
      return (
        <Box className="convert-progress">
          <div className="inprogress-icon-container">
            <CircularProgress size={20} className="inprogress-icon" />
          </div>
        </Box>
      );
    }
    if (tempSuccessIcon) {
      return (
        <div className="success-icon">
          <CheckRoundedIcon sx={{ color: '#007AFF' }} />
        </div>
      );
    }
    return (
      <Tooltip
        sx={{ display: isEnableConvertBtn ? 'block' : 'none' }}
        arrow
        title={t('convertToAudioToListen')}
        placement="top"
      >
        <div className={isEnableConvertBtn ? '' : 'disabled-btn'}>
          <IconButton
            onClick={handleConvertBlockToAudio}
            className="editor-toolbar-btn"
            disabled={!isEnableConvertBtn}
          >
            <SlowMotionVideoRoundedIcon />
          </IconButton>
        </div>
      </Tooltip>
    );
  };

  return (
    <Box className="convert-progress">
      {isBlockEditting ? null : renderSynthesisStatusIcon()}
      {renderPlayOrPauseAudioButton()}
      {renderSynthesisButton()}
    </Box>
  );
};

export default SynthesisButton;
